import type { ReactNode } from 'react';

interface GuideStepCardProps {
  stepNumber: number;
  totalSteps: number;
  title: string;
  description: string;
  nav?: ReactNode;
}

/**
 * Individual guide step card — shows step number, title, and description.
 * Nav (Previous/Next) is slotted between the step indicator and the title.
 */
export function GuideStepCard({
  stepNumber,
  totalSteps,
  title,
  description,
  nav,
}: GuideStepCardProps) {
  return (
    <div className="space-y-3">
      {/* Step indicator */}
      <div className="flex items-center gap-2">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-cyan-100 text-xs font-bold text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300">
          {stepNumber + 1}
        </span>
        <span className="text-xs text-stone-400 dark:text-stone-500">
          of {totalSteps}
        </span>
      </div>

      {nav && <div className="py-2">{nav}</div>}

      {/* Title */}
      <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
        {title}
      </h3>

      {/* Description — blank lines become paragraphs; "## " lines become
          section headers (the final reference card uses them) */}
      {description.split('\n\n').map((paragraph, i) =>
        paragraph.startsWith('## ') ? (
          <h4
            key={i}
            className="pt-2 text-sm font-semibold uppercase tracking-wide text-stone-700 dark:text-stone-300"
          >
            {paragraph.slice(3)}
          </h4>
        ) : (
          <p key={i} className="text-base leading-relaxed text-stone-600 dark:text-stone-400">
            {paragraph}
          </p>
        ),
      )}
    </div>
  );
}
